"use client";

import React, { useRef } from "react";
import { ChevronDown } from "lucide-react";

/**
 * ScrollCue — a bouncing down-arrow shown at the bottom of a
 * full-screen hero. Clicking it smooth-scrolls past the whole hero to
 * the next section.
 *
 * Finds its nearest ancestor <section> at click time and scrolls to
 * that section's bottom edge (= top of the following content). The
 * desktop header is in normal flow (scrolls away), so no offset needed.
 */
export function ScrollCue({ label = "Scroll to content" }: { label?: string }) {
  const ref = useRef<HTMLButtonElement>(null);

  const handleClick = () => {
    const section = ref.current?.closest("section");
    if (!section) return;
    const target = section.getBoundingClientRect().bottom + window.scrollY;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) {
      window.scrollTo(0, target);
      return;
    }

    // Manual rAF tween (easeInOutQuad). Avoids relying on native
    // `behavior:'smooth'`, which some environments don't animate.
    const start = window.scrollY;
    const dist = target - start;
    const duration = 650;
    let t0: number | null = null;
    const step = (ts: number) => {
      if (t0 === null) t0 = ts;
      const p = Math.min(1, (ts - t0) / duration);
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      window.scrollTo(0, start + dist * eased);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      aria-label={label}
      className="absolute bottom-5 left-1/2 z-20 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full text-white ring-1 ring-white/40 transition-colors hover:bg-white/15 motion-safe:animate-bounce"
      style={{ background: "rgba(255,255,255,0.08)" }}
    >
      <ChevronDown className="h-6 w-6" strokeWidth={2.25} />
    </button>
  );
}

export default ScrollCue;
